import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import StatCard from './StatCard';
import { formatEur } from '@/lib/writer/telemetryCost';
import {
  percentile,
  turnCostEur,
  type DaySeriesPoint,
  type RangeTotals,
  type RecentTurnRow,
} from '@/lib/writer/telemetryTypes';

const PALETTE = ['#4f46e5', '#06b6d4', '#f59e0b', '#ef4444', '#10b981'];

interface Props {
  series: DaySeriesPoint[];
  totals: RangeTotals;
  recentTurns: RecentTurnRow[];
}

const CostPanel: React.FC<Props> = ({ series, totals, recentTurns }) => {
  const models = React.useMemo(() => {
    const set = new Set<string>();
    for (const p of series) for (const m of Object.keys(p.costByModel)) set.add(m);
    return [...set];
  }, [series]);

  const chartData = React.useMemo(
    () =>
      series.map((p) => {
        const row: Record<string, number | string> = { day: p.day.slice(5) };
        for (const m of models) row[m] = Number((p.costByModel[m] ?? 0).toFixed(5));
        return row;
      }),
    [series, models],
  );

  const turnCosts = React.useMemo(
    () => recentTurns.map(turnCostEur).filter((c): c is number => c !== null),
    [recentTurns],
  );
  const p50 = percentile(turnCosts, 0.5);
  const p90 = percentile(turnCosts, 0.9);
  const max = turnCosts.length ? Math.max(...turnCosts) : null;

  const expensive = React.useMemo(
    () =>
      [...recentTurns]
        .map((r) => ({ r, cost: turnCostEur(r) ?? 0 }))
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 5),
    [recentTurns],
  );

  const tokens = totals.tokens;
  const tokenMix = [
    { name: 'Input (uncached)', value: Math.max(0, tokens.tokensIn - tokens.tokensCached) },
    { name: 'Cached input', value: tokens.tokensCached },
    { name: 'Thinking', value: tokens.tokensThinking },
    { name: 'Output', value: tokens.tokensOut },
  ];
  const tokenSum = tokenMix.reduce((s, x) => s + x.value, 0) || 1;

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Cost</h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Cost in range" value={formatEur(totals.costEur)} />
        <StatCard label="Cost / turn (avg)" value={formatEur(totals.costPerTurn)} />
        <StatCard
          label="Cost / turn p50 · p90"
          value={`${formatEur(p50)} · ${formatEur(p90)}`}
          hint={`max ${formatEur(max)} (last ${recentTurns.length} turns)`}
        />
        <StatCard
          label="Cost / active user"
          value={formatEur(totals.activeUsers ? totals.costEur / totals.activeUsers : null)}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Cost per day by model</CardTitle>
        </CardHeader>
        <CardContent>
          {models.length === 0 ? (
            <p className="text-sm text-muted-foreground">No priced turns in this range.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => `€${v}`} />
                  <Tooltip formatter={(v: number) => formatEur(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {models.map((m, i) => (
                    <Bar key={m} dataKey={m} stackId="c" fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Token mix</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tokenMix.map((t) => (
              <div key={t.name}>
                <div className="flex justify-between text-sm">
                  <span>{t.name}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {t.value.toLocaleString()} ({((t.value / tokenSum) * 100).toFixed(0)}%)
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded mt-1">
                  <div
                    className="h-1.5 bg-primary rounded"
                    style={{ width: `${(t.value / tokenSum) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground pt-1">
              Cached input bills at ~25% of the input rate — a rising cached share means the stable
              prompt prefix is working.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Most expensive turns</CardTitle>
          </CardHeader>
          <CardContent>
            {expensive.length === 0 ? (
              <p className="text-sm text-muted-foreground">No turns in this range.</p>
            ) : (
              <ul className="text-sm divide-y divide-border/40">
                {expensive.map(({ r, cost }) => (
                  <li key={r.id} className="py-2 flex justify-between gap-3">
                    <span className="truncate">
                      {new Date(r.created_at).toLocaleString()} ·{' '}
                      <span className="text-muted-foreground">
                        {r.project_title ?? 'no project'}
                      </span>
                    </span>
                    <span className="tabular-nums shrink-0">
                      {formatEur(cost)} · {r.tokens_total.toLocaleString()} tok
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default CostPanel;
